"use strict";
var _DataStub_pathParts, _DataStub_path, _DataStub_key, _DataStub_schema, _DataStub_fieldsLUT, _DataStub_fieldsStubLUT;
Object.defineProperty(exports, "__esModule", { value: true });
exports.DataStub = void 0;
const tslib_1 = require("tslib");
const path_1 = require("../utils/path");
const array2object_1 = tslib_1.__importDefault(require("../utils/array2object"));
const getValue_1 = tslib_1.__importDefault(require("../utils/getValue"));
const eval_1 = require("./eval");
const eventemitter3_1 = tslib_1.__importDefault(require("eventemitter3"));
class DataStub extends eventemitter3_1.default {
    //#endregion
    /**
     * 请使用工厂方法创建 DataStub，例如：
     *
     * - `DataStub.fromRoot(myContext, myData)`
     * - `myContext.makeRootStubOf(myData)`
     */
    constructor(opts) {
        var _a, _b, _c;
        super();
        this.readonly = false;
        /** 有些 schema 是含有表达式的（例如 $expr、enumExpr 之类的），这些字段就是不稳定的，当数据变了，需要即时刷新组件 */
        this.isVolatile = false;
        _DataStub_pathParts.set(this, void 0);
        _DataStub_path.set(this, void 0);
        _DataStub_key.set(this, void 0); // 等同于 #pathParts 末尾项
        _DataStub_schema.set(this, void 0);
        /** 同 definedFields 但是是以对象的形式提供的。用于加速 getChildStub */
        _DataStub_fieldsLUT.set(this, void 0);
        /** 迷之缓存 */
        _DataStub_fieldsStubLUT.set(this, void 0);
        this.context = opts.context;
        Object.defineProperty(this, 'context', {
            enumerable: true,
            configurable: false,
            value: opts.context
        });
        let schema = (opts.patchedSchema) ? opts.patchedSchema :
            (opts.rawSchema) ? opts.context.patchSchema(opts.rawSchema) :
                (opts.schemaPath) ? opts.context.getSchema(opts.schemaPath) :
                    null;
        if (!schema)
            throw new Error("cannot get schema");
        tslib_1.__classPrivateFieldSet(this, _DataStub_schema, schema, "f");
        tslib_1.__classPrivateFieldSet(this, _DataStub_pathParts, opts.pathParts, "f");
        tslib_1.__classPrivateFieldSet(this, _DataStub_path, (0, path_1.joinPath)(tslib_1.__classPrivateFieldGet(this, _DataStub_pathParts, "f")), "f");
        this.parent = opts.parent;
        if (this.parent) {
            tslib_1.__classPrivateFieldSet(this, _DataStub_key, opts.pathParts[opts.pathParts.length - 1], "f");
            this.root = this.parent.root;
        }
        this.isVolatile = !!schema.enumExpr;
        switch (this.type) {
            case 'object':
                this.definedFields = opts.definedFields || ((_a = this.schema.fields) === null || _a === void 0 ? void 0 : _a.filter(x => x.id).map(x => this.context.patchSchema(x)).filter(Boolean)) || [];
                tslib_1.__classPrivateFieldSet(this, _DataStub_fieldsLUT, (0, array2object_1.default)(this.definedFields), "f");
                this.additionalFields = this.context.patchSchema(this.schema.additionalFields);
                if (this.additionalFields && this.additionalFields.required) {
                    this.additionalFields = Object.assign(Object.assign({}, this.additionalFields), { required: false });
                }
                this.isVolatile = this.isVolatile || this.definedFields.some(x => !!x.condition) || !!((_b = this.schema.groups) === null || _b === void 0 ? void 0 : _b.some(x => !!x.condition));
                tslib_1.__classPrivateFieldSet(this, _DataStub_fieldsStubLUT, {}, "f");
                break;
            case 'array':
                this.arrItems = (_c = this.context.patchSchema(this.schema.items)) !== null && _c !== void 0 ? _c : { type: "any" };
                break;
        }
    }
    static fromRoot(context, value) {
        const stub = new DataStub({ context, rawSchema: context.source, pathParts: [] });
        stub.root = stub;
        stub.internalRootValue = value;
        return stub;
    }
    /**
     * 从多个 dataStub 合成一个虚拟的，类型为 object 的 dataStub。
     * 这个 dataStub 具备 DataStub 的各种方法可被调用，
     * 例如，你可以通过 getFields 获取到它的子 dataStubs
     *
     * 需要注意：
     */
    static createCompositedDataStub(opts) {
        const firstStub = opts.dataStubs[Object.keys(opts.dataStubs)[0]];
        const definedFields = []; // 虚拟的 fields
        const fieldsLUT = {}; // 真实的每个字段的 schema
        const fieldsStubLUT = {};
        Object.keys(opts.dataStubs).forEach(id => {
            const stub = opts.dataStubs[id];
            definedFields.push(Object.assign(Object.assign({}, stub.schema), { id, required: true }));
            fieldsStubLUT[id] = stub;
            fieldsLUT[id] = stub.schema;
        });
        const patchedSchema = {
            type: "object",
            fields: definedFields
        };
        const ans = new DataStub({
            context: firstStub.context,
            pathParts: [],
            patchedSchema,
            definedFields,
        });
        let rootValue = opts.rootValue;
        if (rootValue === undefined) {
            rootValue = {};
            Object.keys(opts.dataStubs).forEach(id => {
                const stub = opts.dataStubs[id];
                Object.defineProperty(rootValue, id, {
                    enumerable: true,
                    configurable: true,
                    get: () => stub.value,
                    set: (v) => stub.setValue(v)
                });
            });
        }
        ans.root = ans;
        ans.internalRootValue = rootValue;
        tslib_1.__classPrivateFieldSet(ans, _DataStub_fieldsLUT, fieldsLUT, "f");
        tslib_1.__classPrivateFieldSet(ans, _DataStub_fieldsStubLUT, fieldsStubLUT, "f");
        return ans;
    }
    /**
     * 从当前 Stub 创建一个 rootStub，并且将 internalRootValue 指向当前编辑的值。
     *
     * 注意，opts 里 `detached` 默认为 `true`，
     * 这个选项，会将这里新的 DataStub 和原本的做分离，这里的修改不会影响到原来的 Stub，原来的 Stub 修改也不会影响到这里的。
     * 然而，在 **非immutable模式** 下，如果两边保存了同一个引用，则对子项的修改 可能会相互影响。
     * ）
     */
    createdIsolatedDataStub(opts = {}) {
        const { newPath = '', detached = true } = opts;
        const pathParts = Array.isArray(newPath) ? newPath : (0, path_1.splitPath)(newPath);
        const patchedSchema = Object.assign(Object.assign({}, this.schema), { id: String(pathParts[pathParts.length - 1] || '') });
        const stub = new DataStub({
            context: this.context,
            patchedSchema: patchedSchema,
            pathParts: pathParts
        });
        stub.root = stub;
        if (detached) {
            stub.internalRootValue = this.value;
        }
        else {
            Object.defineProperty(stub, 'internalRootValue', {
                get: () => this.getValue(),
                set: val => this.setValue(val)
            });
        }
        return stub;
    }
    /** 这个 stub 对应的 path，例如空值表示 root，`foo.3` 表示 foo 属性(是个数组)的第四个元素 */
    get path() { return tslib_1.__classPrivateFieldGet(this, _DataStub_path, "f"); }
    get type() { return this.schema.type; }
    get value() { return this.getValue(); }
    set value(newVal) { this.setValue(newVal); }
    get schema() { return this.getSchema(); }
    getValue() {
        if (!this.parent)
            return this.internalRootValue;
        if (this.context.autoCompleteMissingFields && this.parent.type !== 'array') {
            // 在需要补全缺失值的情况下，过程会变得复杂一些，只能使用低效率的方式来做：逐层向上查询
            // 注意：补全缺失值时，不要考虑数组项，否则会导致 React 列表渲染里，数组元素删除不掉 -- 一删除就被补全回去了
            let key = tslib_1.__classPrivateFieldGet(this, _DataStub_key, "f");
            const parentValue = this.parent.getValue(); // 此时理论上会递归触发父级的 autoComplete
            if (!parentValue || typeof parentValue !== 'object')
                return undefined; // 父级不是 object
            if (Reflect.has(parentValue, key))
                return parentValue[key];
            // 此时需要做补全
            const defaultValue = this.getDefaultValue();
            if (typeof defaultValue !== 'undefined') {
                this.setValue(defaultValue);
                return this.parent.value[key]; // 因为 immutable 的原因，需要再次读 parent
            }
            return undefined;
        }
        else {
            // 默认情况下，使用最快速的方式返回
            const rootPathPartsLen = tslib_1.__classPrivateFieldGet(this.root, _DataStub_pathParts, "f").length;
            const pathParts = rootPathPartsLen ? tslib_1.__classPrivateFieldGet(this, _DataStub_pathParts, "f").slice(rootPathPartsLen) : tslib_1.__classPrivateFieldGet(this, _DataStub_pathParts, "f");
            return (0, getValue_1.default)(this.root.value, pathParts);
        }
    }
    setValue(newVal, trace) {
        if (this.readonly)
            return;
        if (this.parent) {
            this.parent.setChildValue(tslib_1.__classPrivateFieldGet(this, _DataStub_key, "f"), newVal, trace);
        }
        else {
            const oldValue = this.internalRootValue;
            if (this.context.immutable && oldValue === newVal)
                return;
            this.internalRootValue = newVal;
            this.emit("change", this, oldValue, [...trace || [], { path: "", value: newVal }]);
        }
    }
    getSchema() {
        if (this.isVolatile) {
            // TODO: 处理 isVolatile 的可怕逻辑
        }
        return tslib_1.__classPrivateFieldGet(this, _DataStub_schema, "f");
    }
    /** 获取枚举项目 */
    getEnums() {
        const schema = this.schema;
        if (schema.enum)
            return schema.enum;
        if (schema.enumExpr)
            return (0, eval_1.tryEvalExpr)(schema.enumExpr, null, this, this.context.evalExtraCtx);
        return null;
    }
    /**
     * 获取 **直接** 子元素的 DataStub（仅当前元素是 object / array 的时候有用）
     *
     * @remarks 如果你想通过 `"foo.bar[3]"` 这种表达式取深层子元素，请使用 `getStubAtPath`
     */
    getChildStub(key) {
        var _a;
        let childSchema;
        switch (this.type) {
            case 'array':
                childSchema = this.arrItems;
                break;
            case 'object':
                childSchema = tslib_1.__classPrivateFieldGet(this, _DataStub_fieldsLUT, "f")[key];
                if (!childSchema && this.additionalFields) {
                    childSchema = Object.assign(Object.assign({}, this.additionalFields), { id: String(key) });
                }
                const cachedStub = (_a = tslib_1.__classPrivateFieldGet(this, _DataStub_fieldsStubLUT, "f")) === null || _a === void 0 ? void 0 : _a[key];
                if (cachedStub && cachedStub.schema === childSchema) {
                    return cachedStub;
                }
                break;
        }
        if (!childSchema)
            return undefined;
        const child = new DataStub({
            context: this.context,
            parent: this,
            patchedSchema: childSchema,
            pathParts: [...tslib_1.__classPrivateFieldGet(this, _DataStub_pathParts, "f"), key]
        });
        if (this.type === 'object' && !this.isVolatile && !child.isVolatile) {
            tslib_1.__classPrivateFieldGet(this, _DataStub_fieldsStubLUT, "f")[key] = child;
        }
        return child;
    }
    /**
     * 获取某个路径指向的元素的 DataStub
     *
     * @param path 路径，例如 `"foo.tags[3].type"` 或者 `["foo","tags",3,"type"]`
     */
    getStubAtPath(path) {
        /* istanbul ignore next */
        const pathParts = Array.isArray(path) ? path : (0, path_1.splitPath)(path);
        let ans = this;
        for (let i = 0; ans && i < pathParts.length; i++) {
            ans = ans.getChildStub(pathParts[i]);
        }
        return ans;
    }
    /**
     * 计算默认值
     */
    getDefaultValue(opts) {
        return this.context.getDefaultValue(this, opts);
    }
    /**
     * 仅当前 type 是 object 时有用。根据当前 value 获取要外显的 fields.
     *
     * 因为有 group 和 additionalFields 的概念存在，部分字段不一定会展示，所以这个和 `definedFields` 会不相同
     *
     * @returns - `fields` 是要外显的字段（对于required但是值为undefined的字段也会包含）
     *          - `candidates` 是definedFields定义了但未添加的可选字段
     */
    getFields() {
        /* istanbul ignore next */
        if (this.type !== 'object')
            throw new Error("The schema type of current stub is not object");
        const { schema, value, additionalFields } = this;
        const allKeys = new Set((value && typeof value === 'object' && Object.keys(value)) || []);
        let fields = this.definedFields;
        let candidates = []; // 已经
        // 执行 groups 的 condition
        if (schema.groups && schema.groups.some(x => x.condition)) {
            const supressedGroups = schema.groups && schema.groups
                .filter(x => x.condition && !(0, eval_1.tryEvalExpr)(x.condition, false, this, this.context.evalExtraCtx))
                .map(x => x.id);
            fields = fields.filter(x => !x.group || !supressedGroups.includes(x.group));
        }
        // 执行 fields 的 condition
        fields = fields.filter(x => !x.condition || (0, eval_1.tryEvalExpr)(x.condition, false, this, this.context.evalExtraCtx));
        // 过滤可选，且未存在的项
        for (let i = 0; i < fields.length; i++) {
            const field = fields[i];
            const isInKeys = allKeys.has(field.id);
            if (!isInKeys && !field.required) {
                // 数据中不存在，而且是可选
                fields.splice(i, 1);
                candidates.push(field);
                i--;
            }
            // 标记 key 已遍历
            if (isInKeys)
                allKeys.delete(field.id);
        }
        // 执行 additionalFields。此时 allKeys 是剩余的多出来的 key
        if (additionalFields) {
            allKeys.forEach(id => { fields.push(Object.assign(Object.assign({}, additionalFields), { id })); });
            allKeys.clear();
        }
        return { fields, candidates };
    }
    /**
     * 修改一个子元素的值（仅当前元素是 object / array 的时候有用）
     */
    setChildValue(key, childValue, trace) {
        const newTrace = [
            ...trace || [],
            { path: (0, path_1.joinPath)([...tslib_1.__classPrivateFieldGet(this, _DataStub_pathParts, "f"), key]), value: childValue }
        ];
        switch (this.schema.type) {
            case 'array':
            case 'object':
                {
                    const newData = this.prepareValueForChange();
                    this.context.setter(newData, key, childValue);
                    this.setValue(newData, newTrace);
                }
                break;
            /* istanbul ignore next */
            default:
                throw new Error(`stub at "${this.path}" is not object / array`);
        }
    }
    /**
     * 如果你要修改 value，而当前是一个 object 或者 array，你需要考虑 immutable 的问题
     *
     * 在 immutable 模式下，你需要准备一个副本；否则，可直接在原对象上操作
     *
     * 这个就是返回一个你能够用来修改，然后喂给 setValue 的对象。
     *
     * @see setChildValue
     */
    prepareValueForChange() {
        const immutable = this.context.immutable;
        switch (this.schema.type) {
            case 'array':
                return immutable ? Array.from(this.value || this.getDefaultValue()) : (this.value || []);
            case 'object':
                return immutable ? Object.assign({}, this.value || this.getDefaultValue()) : (this.value || {});
            /* istanbul ignore next */
            default:
                return this.value;
        }
    }
    /**
     * 获取一个简短的字符串，可大概概括当前的 value 是什么，例如 `[ 3 个元素 ]` 或者 `{ name: xxxx ... }`
     */
    getSummary() {
        var _a;
        const { value, type } = this;
        if (type === 'array') {
            return `[ ${(_a = value === null || value === void 0 ? void 0 : value.length) !== null && _a !== void 0 ? _a : 0} 个${this.arrItems.title || '元素'} ]`;
        }
        if (type === 'object') {
            const firstField = this.getFields().fields.find(field => /^(?:string|boolean|number|integer)$/.test(field.type));
            if (!firstField)
                return `{ ... }`;
            const id = firstField.id;
            return `{ ${id}: ${String(value === null || value === void 0 ? void 0 : value[id]).substr(0, 32)} ... }`;
        }
        let ans = String(value);
        if (ans.length > 32)
            return ans.substr(0, 32) + '...';
        return ans;
    }
    /* istanbul ignore next */
    toString() {
        return `[DataStub @ ${this.path}: ${this.getSummary()}]`;
    }
}
exports.DataStub = DataStub;
_DataStub_pathParts = new WeakMap(), _DataStub_path = new WeakMap(), _DataStub_key = new WeakMap(), _DataStub_schema = new WeakMap(), _DataStub_fieldsLUT = new WeakMap(), _DataStub_fieldsStubLUT = new WeakMap();
//# sourceMappingURL=DataStub.js.map